import React, { useMemo } from "react";
import { BOTTOM_NAVIGATOR_TRADE_CARD_WIDTH } from "features/dashboard/common/constants";
import { getFormattedChartXValue } from "@shared/utils/charts";
import LineChart from "common/Charts/LineChart/LineChart";
import {
  FormattedChartDataPoint,
  LineAnnotation,
  PointAnnotation,
} from "@shared/types";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";

interface TradeChartProps {
  formattedGraphData: FormattedChartDataPoint[];
  currentPrice?: number;
  precision: number;
  isLoading?: boolean;
  annotations: LineAnnotation[];
}

const TradeChart = ({
  formattedGraphData,
  currentPrice,
  precision,
  isLoading,
  annotations,
}: TradeChartProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);

  const currentPriceWidth = useMemo(() => {
    let formattedPrice = currentPrice?.toString();
    if (formattedPrice && precision) {
      formattedPrice =
        formattedPrice.split(".")[0] + "." + "0".repeat(precision);
    }
    return formattedPrice ? formattedPrice?.length * 7 : 0;
  }, [currentPrice]);

  const pointAnnotation: PointAnnotation = useMemo(() => {
    const lastPointData = formattedGraphData?.[formattedGraphData.length - 1];
    return {
      type: "point",
      xValue: lastPointData?.x,
      yValue: lastPointData?.y,
      backgroundColor: "#b3c9fb",
      borderColor: "#3272FE",
      borderWidth: 2,
      radius: 4,
      adjustScaleRange: false,
      timestamp: lastPointData?.rawX,
    };
  }, [formattedGraphData]);

  const getXMax = () => {
    if (formattedGraphData.length < 2) return;
    const numPoints = formattedGraphData.length;
    const lastIndex = numPoints - 1;
    const lastPointRawX = formattedGraphData[lastIndex].rawX;
    const diff = lastPointRawX - formattedGraphData[lastIndex - 1].rawX;
    const chartFullWidth = BOTTOM_NAVIGATOR_TRADE_CARD_WIDTH - PADDING * 2;
    const pointWidth = chartFullWidth / numPoints;
    const pointsToAdd = currentPriceWidth / pointWidth;
    const xMax = lastPointRawX + diff * pointsToAdd;
    return getFormattedChartXValue(xMax) as string;
  };

  return (
    <LineChart
      data={formattedGraphData}
      loading={isLoading}
      annotations={[...annotations, pointAnnotation]}
      extendToAnnotations={true}
      displayCurrency={displayCurrency}
      precision={precision}
      isSmallChart={true}
      isWebsocket={true}
      hideXAxis={true}
      hideYAxis={true}
      hideGradientBg={true}
      hideTooltip={true}
      addPadding={true}
      xMax={getXMax()}
      lastDataPointLabel={currentPrice?.toString()}
      style={{
        margin: "8px 0 4px",
        height: CHART_HEIGHT,
      }}
    />
  );
};

export default React.memo(TradeChart);

const PADDING = 12;
const CHART_HEIGHT = 92;
